import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Accessibility, type IAccessibilityOptions } from 'accessibility';
import { injectSpeechMenuItemsSynchronously } from './speech-menu-items';
import './accessibility-widget.css';

/**
 * תפריט הנגישות של האזור האישי (ספריית accessibility, רישיון MIT).
 *
 * הווידג'ט הוא שכבת הסיוע למשתמש בלבד - העמידה בת"י 5568 (WCAG 2.1 AA)
 * נשענת על הקוד עצמו. ראו את הצהרת הנגישות באתר התדמית: /accessibility.
 */

const LABELS: Record<'he' | 'en', IAccessibilityOptions['labels']> = {
  he: {
    menuTitle: 'תפריט נגישות',
    resetTitle: 'איפוס הגדרות הנגישות',
    closeTitle: 'סגירת תפריט הנגישות',
    increaseText: 'הגדלת גודל הטקסט',
    decreaseText: 'הקטנת גודל הטקסט',
    increaseTextSpacing: 'הגדלת ריווח האותיות',
    decreaseTextSpacing: 'הקטנת ריווח האותיות',
    increaseLineHeight: 'הגדלת ריווח השורות',
    decreaseLineHeight: 'הקטנת ריווח השורות',
    invertColors: 'היפוך צבעים',
    grayHues: 'גווני אפור',
    bigCursor: 'סמן עכבר גדול',
    readingGuide: 'סרגל קריאה',
    underlineLinks: 'הדגשת קישורים',
    textToSpeech: 'הקראת טקסט',
    speechToText: 'דיבור להקלדה',
    disableAnimations: 'עצירת אנימציות',
    hotkeyPrefix: 'קיצור מקלדת: ',
  },
  en: {
    menuTitle: 'Accessibility menu',
    resetTitle: 'Reset accessibility settings',
    closeTitle: 'Close accessibility menu',
    increaseText: 'Increase text size',
    decreaseText: 'Decrease text size',
    increaseTextSpacing: 'Increase letter spacing',
    decreaseTextSpacing: 'Decrease letter spacing',
    increaseLineHeight: 'Increase line height',
    decreaseLineHeight: 'Decrease line height',
    invertColors: 'Invert colors',
    grayHues: 'Gray hues',
    bigCursor: 'Big cursor',
    readingGuide: 'Reading guide',
    underlineLinks: 'Underline links',
    textToSpeech: 'Text to speech',
    speechToText: 'Speech to text',
    disableAnimations: 'Disable animations',
    hotkeyPrefix: 'Keyboard shortcut: ',
  },
};

/** כתובת מלאה - האזור האישי יושב תחת /new, וההצהרה חיה באתר התדמית. */
const STATEMENT_URL = 'https://www.hatzdaka.org/accessibility';

const LANDMARK_ID = 'a11y-widget-landmark';

/**
 * הספרייה מזריקה DOM גלובלי, ולכן צריך מופע יחיד. ב-StrictMode ה-effect רץ
 * פעמיים בפיתוח, וללא השמירה הזו היו נוצרים שני כפתורי נגישות.
 */
let instance: InstanceType<typeof Accessibility> | null = null;

/**
 * הכפתור והתפריט מוזרקים ישירות ל-body, מחוץ לכל landmark, ובדיקת נגישות
 * אוטומטית מסמנת אותם כתוכן שאינו מוכל באזור. הבנייה מתרחשת רק אחרי
 * שפונט האייקונים נטען, ולכן צריך observer ולא קריאה מיידית.
 */
const wrapWidgetInLandmark = (label: string): (() => void) => {
  const attach = (): boolean => {
    const parts = document.querySelectorAll<HTMLElement>('._access-icon, ._access-menu');
    if (parts.length < 2) return false;

    const landmark = document.createElement('div');
    landmark.id = LANDMARK_ID;
    landmark.setAttribute('role', 'complementary');
    landmark.setAttribute('aria-label', label);
    document.body.appendChild(landmark);
    parts.forEach((part) => landmark.appendChild(part));

    // הספרייה שמה title על האייקון, שמציג את טולטיפ הדפדפן. מעבירים אותו
    // ל-data-tooltip (המעוצב כמו של כפתור הפידבק) ול-aria-label, כדי שלא
    // יוצגו שני טולטיפים וקורא מסך עדיין יקבל את השם. ה-observer נשאר
    // חי כדי שגם אם ה-title יופיע שוב הוא יועבר מיד.
    const transferIconTitle = () => {
      const icon = landmark.querySelector<HTMLElement>('._access-icon');
      const title = icon?.getAttribute('title');
      if (icon && title) {
        icon.setAttribute('data-tooltip', title);
        icon.setAttribute('aria-label', title);
        icon.removeAttribute('title');
      }
    };
    transferIconTitle();
    new MutationObserver(transferIconTitle).observe(landmark, {
      subtree: true,
      attributes: true,
      attributeFilter: ['title'],
    });
    return true;
  };

  if (attach()) return () => document.getElementById(LANDMARK_ID)?.remove();

  const observer = new MutationObserver(() => {
    if (attach()) observer.disconnect();
  });
  observer.observe(document.body, { childList: true });

  return () => {
    observer.disconnect();
    document.getElementById(LANDMARK_ID)?.remove();
  };
};

const AccessibilityWidget = () => {
  const { i18n } = useTranslation();
  const isHebrew = i18n.language !== 'en';

  useEffect(() => {
    // החלפת שפה בונה את התפריט מחדש כדי שהתוויות יתורגמו איתה.
    instance?.destroy();
    instance = null;
    document.getElementById(LANDMARK_ID)?.remove();

    // חייב לקדום לבנאי: הוא זה שמזריק את שורות הדיבור לתפריט.
    injectSpeechMenuItemsSynchronously();

    instance = new Accessibility({
      icon: {
        img: 'accessibility',
        // אם גוגל חסומה או שהפונט לא נטען, הספרייה נופלת חזרה לאימוג'י
        // במקום להציג ריבוע ריק - כפתור בלתי מזוהה גרוע מכפתור פשוט.
        fontFamilyValidation: 'Material Icons',
        tabIndex: 0,
      },
      labels: isHebrew ? LABELS.he : LABELS.en,
      language: {
        textToSpeechLang: isHebrew ? 'he-IL' : 'en-US',
        speechToTextLang: isHebrew ? 'he-IL' : 'en-US',
      },
      textToSpeechLang: isHebrew ? 'he-IL' : 'en-US',
      modules: {
        increaseText: true,
        decreaseText: true,
        increaseTextSpacing: true,
        decreaseTextSpacing: true,
        increaseLineHeight: true,
        decreaseLineHeight: true,
        invertColors: true,
        grayHues: true,
        bigCursor: true,
        readingGuide: true,
        underlineLinks: true,
        textToSpeech: true,
        // כבוי בכוונה: הקלדה קולית מבקשת הרשאת מיקרופון ואינה נדרשת כאן.
        speechToText: false,
        disableAnimations: true,
      },
      session: { persistent: true },
      statement: { url: STATEMENT_URL },
    });

    const detachLandmark = wrapWidgetInLandmark(isHebrew ? 'כלי נגישות' : 'Accessibility tools');

    return () => {
      // סדר חשוב: destroy מסיר את האלמנטים מתוך העטיפה, ורק אז מסירים אותה.
      instance?.destroy();
      instance = null;
      detachLandmark();
    };
  }, [isHebrew]);

  return null;
};

export default AccessibilityWidget;
